import { useEffect, useMemo } from 'react';
import { useExperiments } from '@johnlewispartnership/wtr-website-decisioner-client/express';
import { useMediaQuery } from 'react-responsive';
import { SlotType } from 'services/slotOrchestration/types';
import { useTrolley } from 'contexts/Trolley';
import { useSlotContext } from 'contexts/Slot';
import { EOS_NEW_HEADER } from 'constants/monetateExperiments';
import { BREAKPOINTS } from 'constants/grid';

class MinSpendStylesManager {
  private minSpendHeight = '0px';

  private minSpendSmHeight = '0px';

  private slotsTableOffset = '0px';

  updateStyles(minSpendHeight: string, minSpendSmHeight: string, slotsTableOffset: string): void {
    if (
      this.minSpendHeight !== minSpendHeight ||
      this.minSpendSmHeight !== minSpendSmHeight ||
      this.slotsTableOffset !== slotsTableOffset
    ) {
      this.minSpendHeight = minSpendHeight;
      this.minSpendSmHeight = minSpendSmHeight;
      this.slotsTableOffset = slotsTableOffset;

      document.body.style.setProperty('--global-min-spend-height', minSpendHeight);
      document.body.style.setProperty('--global-min-spend-height-sm', minSpendSmHeight);
      document.body.style.setProperty('--global-slots-table-offset', slotsTableOffset);
    }
  }
}

export const minSpendStylesManager = new MinSpendStylesManager();

export function useMinSpend() {
  const { getExperimentById } = useExperiments();
  const showNewHeader =
    getExperimentById(EOS_NEW_HEADER.experiment)?.variant === EOS_NEW_HEADER.variant.showNewHeader;
  const { state: trolleyState } = useTrolley();
  const {
    slotContext: { details: slotDetails },
  } = useSlotContext();
  const isMobileBreakpoint = useMediaQuery({ maxWidth: BREAKPOINTS.md });
  const isTabletBreakpoint = useMediaQuery({ minWidth: BREAKPOINTS.md, maxWidth: BREAKPOINTS.lg });
  const isDesktopBreakpoint = useMediaQuery({ minWidth: BREAKPOINTS.lg });

  const { slotType = SlotType.UNDEFINED } = slotDetails || {};

  const collectionMinimumOrderValue =
    trolleyState?.trolley?.trolleyTotals?.collectionMinimumOrderValue?.amount;
  const deliveryMinimumOrderValue =
    trolleyState?.trolley?.trolleyTotals?.deliveryMinimumOrderValue?.amount;
  const mimiumSpendThresholdMet = trolleyState?.trolley?.trolleyTotals?.minimumSpendThresholdMet;

  const minSpend = useMemo(() => {
    let minimumSpend = 0;

    if (slotType === SlotType.DELIVERY && deliveryMinimumOrderValue) {
      minimumSpend = deliveryMinimumOrderValue;
    } else if (slotType === SlotType.GROCERY_COLLECTION && collectionMinimumOrderValue) {
      minimumSpend = collectionMinimumOrderValue;
    }
    return minimumSpend;
  }, [collectionMinimumOrderValue, deliveryMinimumOrderValue, slotType]);

  const showMinSpend = useMemo(
    () =>
      mimiumSpendThresholdMet !== undefined &&
      !mimiumSpendThresholdMet &&
      slotType !== SlotType.ENTERTAINING_COLLECTION &&
      slotType !== SlotType.UNDEFINED,
    [mimiumSpendThresholdMet, slotType],
  );

  const minSpendHeight = useMemo(() => {
    let height = '0px';
    if (!showMinSpend) height = '0px';
    if (!showMinSpend && showNewHeader) height = '-1px';
    if (showMinSpend) height = '24px';
    if (showMinSpend && showNewHeader) height = '9px';
    if (showMinSpend && showNewHeader && isTabletBreakpoint) height = '15px';
    return height;
  }, [showMinSpend, showNewHeader, isTabletBreakpoint]);

  const minSpendSmHeight = useMemo(() => {
    let height = '0px';
    if (!showMinSpend) height = '0px';
    if (showMinSpend) height = '4px';
    if (showMinSpend && showNewHeader) height = '60px';
    if (!showMinSpend && showNewHeader) height = '51px';
    return height;
  }, [showMinSpend, showNewHeader]);

  const slotsTableOffset = useMemo(() => {
    let offset = '0px';
    if (!showMinSpend) offset = '0px';
    if (showMinSpend) offset = '9px';
    if (showMinSpend && (isDesktopBreakpoint || isTabletBreakpoint)) offset = '24px';
    if (showMinSpend && isMobileBreakpoint) offset = '4px';
    if (!showMinSpend && showNewHeader) offset = '9px';
    if (!showMinSpend && showNewHeader && isMobileBreakpoint) offset = '51px';
    if (!showMinSpend && showNewHeader && (isDesktopBreakpoint || isTabletBreakpoint))
      offset = '0px';
    if (showMinSpend && showNewHeader && isMobileBreakpoint) offset = '61px';
    if (showMinSpend && showNewHeader && isTabletBreakpoint) offset = '16px';
    if (showMinSpend && showNewHeader && isDesktopBreakpoint) offset = '14px';
    return offset;
  }, [showMinSpend, showNewHeader, isMobileBreakpoint, isTabletBreakpoint, isDesktopBreakpoint]);

  useEffect(() => {
    minSpendStylesManager.updateStyles(minSpendHeight, minSpendSmHeight, slotsTableOffset);
  }, [minSpendHeight, minSpendSmHeight, slotsTableOffset]);

  return {
    minSpend,
    showMinSpend,
  };
}
